import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import adminApi from "@diditusa/shared/api/adminApi";
import { useParams } from "react-router-dom";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { getErrorMessage } from "@diditusa/shared/api/apiUtils";
import { OptionPricingStatus } from "@diditusa/shared/types/productTypes";

export default function ProductDetails() {
  const { productId } = useParams();
  const { data: product, ...getProductState } =
    adminApi.useAdminGetProductByIdQuery(parseInt(productId!));
  const [createPricingOption, createPricingOptionState] =
    adminApi.useAdminCreateProductPricingMutation();
  const [setPricingActive, setPricingActiveState] =
    adminApi.useAdminSetProductPricingActiveMutation();
  const [selectedPrice, setSelectedPrice] = useState("1500");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPrice, setNewPrice] = useState("");

  useEffect(() => {
    if (createPricingOptionState.isSuccess) {
      // Reset and close
      setNewPrice("");
      setIsDialogOpen(false);
    }
  }, [createPricingOptionState.isSuccess]);

  const handleAddPriceOption = () => {
    const priceInCents = Math.round(parseFloat(newPrice) * 100);
    if (isNaN(priceInCents)) {
      alert("Please enter a valid number");
      return;
    }

    createPricingOption({ productId: parseInt(productId!), priceInCents });
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPrice((event.target as HTMLInputElement).value);
  };

  if (getProductState.isLoading) {
    return <LoadingIndicator isVisible={getProductState.isLoading} />;
  }

  if (getProductState.isError) {
    return (
      <Typography>{`Error loading product: ${
        (getProductState.error as any).data.message
      }`}</Typography>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#F9FAFA",
        minHeight: "100vh",
        fontFamily: 'Inter, "Noto Sans", sans-serif',
      }}
    >
      <Container>
        <Box py={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} color="#1C1D22">
                Product Details
              </Typography>
              <Typography fontSize={14} color="#3C3F4A">
                View detailed information about the product.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              sx={{ borderRadius: 5, height: 32, fontSize: 14 }}
            >
              Edit
            </Button>
          </Box>

          <TextField
            label="Product Title"
            fullWidth
            variant="outlined"
            sx={{ mb: 3, background: "#fff", borderRadius: 2 }}
            value={product?.title}
          />

          <TextField
            label="Product Description"
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            sx={{ mb: 3, background: "#fff", borderRadius: 2 }}
            value={product!.description}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight={700} color="#1C1D22">
              Pricing Options
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsDialogOpen(true)}
              sx={{ textTransform: "none", borderRadius: 5 }}
            >
              Add Price
            </Button>
          </Box>

          {/* <FormControl component="fieldset">
            <RadioGroup value={selectedPrice} onChange={handlePriceChange}>
              {product?.optionPricing.map(({ unitAmount, status }) => {
                const price = unitAmount / 100;
                return (
                  <FormControlLabel
                    key={price}
                    value={price}
                    control={<Radio sx={{ color: "#607AFB" }} />}
                    label={
                      <Box display="flex" flexDirection="column">
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          color="#1C1D22"
                        >
                          ${price}
                        </Typography>
                        <Typography fontSize={14} color="#3C3F4A">
                          {status}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: "1px solid #D5D6DD",
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                      mb: 2,
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl> */}
          <LoadingIndicator isVisible={setPricingActiveState.isLoading} />
          {product?.optionPricing.map((option) => {
            const price = option.unitAmount / 100;
            const isActive = option.status === OptionPricingStatus.ACTIVE;
            console.log(`${option.id} isActive ${isActive}`);
            const handleToggle = () => {
              setPricingActive({
                productId: parseInt(productId!),
                optionPricingId: option.id,
              });
            };

            return (
              <Box
                key={option.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border="1px solid #D5D6DD"
                borderRadius={2}
                px={2}
                py={1.5}
                mb={2}
                bgcolor="#fff"
              >
                <Box>
                  <Typography fontSize={14} fontWeight={500} color="#1C1D22">
                    ${price}
                  </Typography>
                  <Typography fontSize={14} color="#3C3F4A">
                    {option.status}
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={isActive}
                  onChange={handleToggle}
                  disabled={isActive} // Optional: disable if already active
                />
              </Box>
            );
          })}
          {setPricingActiveState.isError && (
            <p>{getErrorMessage(setPricingActiveState.error)}</p>
          )}

          <Box mt={4}>
            {product?.imageUrl ? (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  overflow: "hidden",
                  aspectRatio: "3 / 2",
                  backgroundImage: `url("${product?.imageUrl}")`,
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                }}
              ></Box>
            ) : (
              <Box>
                <Typography>No image</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Add New Price</DialogTitle>
        <DialogContent>
          <TextField
            label="New Price (USD)"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            type="number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddPriceOption}
            variant="contained"
            disabled={createPricingOptionState.isLoading}
          >
            Save
          </Button>
          {createPricingOptionState.isLoading && (
            <LoadingIndicator isVisible={createPricingOptionState.isLoading} />
          )}
        </DialogActions>
        {createPricingOptionState.isError && (
          <p>{getErrorMessage(createPricingOptionState.error)}</p>
        )}
      </Dialog>
    </Box>
  );
}
